/**
 * Shrinks user-picked photos in the browser before they are uploaded.
 *
 * A headshot straight from a phone camera is routinely 4000px wide and several
 * megabytes, but it is only ever displayed in a small avatar. Re-encoding it to
 * a bounded dimension turns a slow multi-megabyte upload on mobile data into a
 * few hundred kilobytes, with no visible difference at the sizes we render.
 *
 * Every failure path returns the original file untouched — a browser that
 * cannot decode, resize or re-encode the image must still be able to upload it,
 * since the API accepts the full-size file anyway.
 */

const DEFAULTS = {
  /** Longest edge of the output, in pixels. */
  maxDimension: 1600,
  /** Stop compressing once the result is under this. */
  targetBytes: 1.5 * 1024 * 1024,
  /** Never degrade JPEG quality past this looking for a smaller file. */
  minQuality: 0.6,
  startQuality: 0.85,
};

/**
 * Decodes a file to something drawable. `createImageBitmap` is preferred
 * because `imageOrientation: "from-image"` applies the EXIF rotation phone
 * cameras rely on — without it, portrait photos come out sideways once drawn
 * to a canvas.
 */
const decodeImage = async (file) => {
  if (typeof createImageBitmap === "function") {
    try {
      return await createImageBitmap(file, { imageOrientation: "from-image" });
    } catch {
      // Older Safari rejects the options argument; fall through to <img>.
    }
  }

  return new Promise((resolve, reject) => {
    const objectUrl = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };
    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Could not decode image"));
    };
    image.src = objectUrl;
  });
};

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob(resolve, type, quality));

const withJpgExtension = (name = "photo") =>
  `${name.replace(/\.[^./\\]+$/, "") || "photo"}.jpg`;

/**
 * Returns a resized JPEG `File`, or the original file when resizing is
 * unnecessary or unavailable. Safe to call on any file — non-images pass
 * straight through.
 */
export const resizeImageForUpload = async (file, options = {}) => {
  const { maxDimension, targetBytes, minQuality, startQuality } = {
    ...DEFAULTS,
    ...options,
  };

  if (!file || typeof file.type !== "string" || !file.type.startsWith("image/")) {
    return file;
  }

  let source;
  try {
    source = await decodeImage(file);
  } catch {
    return file;
  }

  try {
    const width = source.width;
    const height = source.height;
    if (!width || !height) return file;

    const scale = Math.min(1, maxDimension / Math.max(width, height));

    // Already small in both dimensions and bytes — sending the original avoids
    // a pointless re-encode (and the generation loss that comes with it).
    if (scale === 1 && file.size <= targetBytes) return file;

    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);

    const context = canvas.getContext("2d");
    if (!context) return file;
    context.drawImage(source, 0, 0, canvas.width, canvas.height);

    let quality = startQuality;
    let blob = await canvasToBlob(canvas, "image/jpeg", quality);

    while (blob && blob.size > targetBytes && quality > minQuality) {
      quality = Math.max(minQuality, quality - 0.1);
      blob = await canvasToBlob(canvas, "image/jpeg", quality);
    }

    // Re-encoding a small graphic (a flat PNG, say) can come out bigger than
    // the original. Keep whichever is smaller.
    if (!blob || blob.size >= file.size) return file;

    return new File([blob], withJpgExtension(file.name), {
      type: "image/jpeg",
      lastModified: Date.now(),
    });
  } catch {
    return file;
  } finally {
    if (typeof source.close === "function") source.close();
  }
};

export default resizeImageForUpload;
